/**
 * 外壳页面：布局/导航契约（data-testid 是 e2e 锚点）。
 * chats Tab 已接真会话列表（ConversationList，任务 8d-2）、contacts Tab 已接
 * 通讯录主页（ContactsPage，任务 14.18b）、me Tab 已接我的页（MeTab，14.29b）。
 * 登录页已是真实现（LoginPage.tsx，任务 6d）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import styles from './shell.module.scss';
import { ConversationList } from './ConversationList';
import { ContactsPageLazy } from './ContactsPageLazy';
import { MeTabLazy } from './MeTabLazy';
import { MessageList } from './MessageList';
import { Composer, type ComposerEditDraft } from './Composer';
import { AfterViewControl } from './AfterViewControl';
import { EncryptionControl } from './EncryptionControl';
import { contactsConnection, conversationsConnection, messagesConnection } from '~/store/appWorker';
import { replySnapshotRef, type ReplyRef } from '~/chat/replyModel';
import type { MessageItemVM } from '~/chat/messageModel';
import { isMediaBlocked, UNRESTRICTED } from '~/chat/groupRestrictionModel';
import { MsgType } from '~/chat/msgTypes';
import { buildContactCardEnvelope } from '~/chat/preview';
import { resolveMediaUrl, stripMediaDomain } from '~/chat/mediaUrl';
import { ContactCardPickerLazy } from './ContactCardPickerLazy';
import type { ContactFriend } from '~/worker/contract';
import type { ConversationKey } from '~/worker/ws/messages';
import { blockedMediaText, filterBlockedAttachments } from './composerModel';
import { useKeyboardInset } from './useKeyboardInset';
import { useImageSender } from './useImageSender';
import { useImageDrop } from './useImageDrop';
import { ImageConfirmSheetLazy } from './ImageConfirmSheetLazy';
import { appRouter } from '~/nav/appRouter';
import {
  openContactSearch,
  openCreateGroup,
  openFriendDetail,
  openGroupSettings,
  switchHomeTab,
  toggleConversationInfo,
} from '~/nav/actions';
import type { HomeTab } from '~/nav/routes';
import { contactsSummaryText, friendCountOf } from './contactsSummary';
import { applyBadgeCount } from './applyEntryModel';
import { activePanelRoute } from '~/nav/projection';
import { focusComposerOnBodyClick } from './focusComposer';
import { useGlobal } from '~/store/useGlobal';
import { avatarStyle } from '~/chat/avatarColor';
import { groupVipBadge, premiumNoBadge, userVipBadge } from '~/chat/vipBadge';
import { PremiumBadge, VipBadge } from './VipBadge';
import { dmSubtitle, friendAccountOf, friendNameOf, groupSubtitle } from '~/chat/chatHeaderModel';
import type { GroupProfileSnapshot } from '~/worker/contract';
import { endInteraction, INTERACTIONS } from '~/perf/interaction';
import { useNowMinute } from './useNowMinute';
import { BRAND_NAME } from '~/brand';
import { BackButton } from '~/ui/BackButton';
import { IconGroupAdd, IconPersonAdd, IconRefresh } from '~/ui/icons';
import {
  offlineTimeOfFriend,
  peerUidOfKey,
  presenceOf,
  presenceStatusText,
} from '~/chat/presenceModel';

const TAB_META: ReadonlyArray<{ tab: HomeTab; title: string }> = [
  { tab: 'chats', title: BRAND_NAME },
  { tab: 'contacts', title: '通讯录' },
  { tab: 'me', title: '我的' },
];

const TAB_LABELS: Record<HomeTab, string> = { chats: '消息', contacts: '通讯录', me: '我的' };

/**
 * 首页头部的三件套动作（21ab 会话列表 / 21ae 通讯录共用）：刷新 / 添加好友 /
 * 发起群聊。两处按钮形态与行为完全一致，只差刷新打到哪条链与各自的 testid，
 * 所以**合成一个组件**——两份各写一遍白占首屏预算（首屏 JS 卡在 80KB 线上）。
 *
 * 刷新只是「让对应的 Worker 链重新对账」——不是重开 session，也不清本地；
 * 转圈只是**给用户一个动作有响应的反馈**，最短转 600ms 再停，
 * 否则本地直出那一下按钮闪一下像没反应。
 */
function HomeHeaderActions({ scope }: { scope: 'chats' | 'contacts' }) {
  const chats = scope === 'chats';
  const [spinning, setSpinning] = useState(false);
  const onRefresh = useCallback(() => {
    if (spinning) return;
    setSpinning(true);
    const started = Date.now();
    const done = () => {
      const rest = Math.max(0, 600 - (Date.now() - started));
      window.setTimeout(() => setSpinning(false), rest);
    };
    if (chats) void conversationsConnection().refresh().finally(done);
    else void contactsConnection().refresh().finally(done);
  }, [spinning, chats]);
  return (
    <span className={styles.headerActions}>
      <button
        type="button"
        className={styles.headerAction}
        data-testid={chats ? 'chats-refresh' : 'contacts-refresh'}
        data-spinning={spinning ? 'true' : undefined}
        aria-label={chats ? '刷新会话列表' : '刷新通讯录'}
        title="刷新"
        onClick={onRefresh}
      >
        <IconRefresh class={styles.headerIcon} />
      </button>
      <button
        type="button"
        className={styles.headerAction}
        data-testid={chats ? 'chats-add-friend' : 'contact-search-entry'}
        aria-label="添加好友"
        title="添加好友"
        onClick={() => openContactSearch(appRouter())}
      >
        <IconPersonAdd class={styles.headerIcon} />
      </button>
      <button
        type="button"
        className={styles.headerActionAccent}
        data-testid={chats ? 'chats-create-group' : 'create-group-entry'}
        aria-label={chats ? '发起群聊' : '创建群聊'}
        title={chats ? '发起群聊' : '创建群聊'}
        onClick={() => openCreateGroup(appRouter())}
      >
        <IconGroupAdd class={styles.headerIcon} />
      </button>
    </span>
  );
}

/**
 * 通讯录头（21ae；旧版桌面面板 `_buildHeader` 同位同形）：标题下压一行
 * 「N 位好友 · M 个群聊」+ 上面那三个图标。
 * 刷新 = Worker 丢三份缓存推 null → 页面「null 则 load」重拉（contactsState.refresh）。
 */
function ContactsHeader() {
  const book = useGlobal((s) => s.contacts.book);
  const groupChats = useGlobal((s) => s.contacts.groupChats);
  const friendCount = useMemo(() => friendCountOf(book), [book]);
  return (
    <>
      <span className={styles.headerTitleBox}>
        <span>通讯录</span>
        <span className={styles.headerSubtitle} data-testid="contacts-summary">
          {contactsSummaryText(friendCount, groupChats?.length ?? 0)}
        </span>
      </span>
      <HomeHeaderActions scope="contacts" />
    </>
  );
}

export function HomePage({ tab }: { tab: HomeTab }) {
  const title = TAB_META.find((m) => m.tab === tab)?.title ?? BRAND_NAME;
  const isMobileLayout = useGlobal((s) => s.layout.isMobile);
  return (
    <div className={styles.page} data-testid={`page-${tab}`}>
      <header className={styles.pageHeader}>
        {/* 通讯录头自带标题（要带副标题），其余 Tab 还是一行标题 */}
        {tab === 'contacts' ? <ContactsHeader /> : title}
        {/* 会话列表头部三件套（21ab，人工给图指出缺这几个；旧版
            chat_list_screen 同位：刷新 / 加好友 / 建群） */}
        {tab === 'chats' && <HomeHeaderActions scope="chats" />}
      </header>
      {tab === 'chats' ? (
        <div className={styles.pageBodyFill}>
          <ConversationList />
        </div>
      ) : tab === 'contacts' ? (
        <div className={styles.pageBodyFill}>
          <ContactsPageLazy />
        </div>
      ) : (
        <div className={styles.pageBodyFill}>
          <MeTabLazy />
        </div>
      )}
      {/* 底部 Tab **仅移动端**（21n）：桌面走左侧图标竖栏 DesktopNavRail */}
      {isMobileLayout && <HomeTabBar active={tab} />}
    </div>
  );
}

function HomeTabBar({ active }: { active: HomeTab }) {
  // 申请红点（21ag）：桌面竖栏 DesktopNavRail 同口径同数据源——两端互斥渲染，
  // 红点不能只做一端
  const applies = applyBadgeCount(useGlobal((s) => s.contacts.points));
  return (
    <nav className={styles.tabBar}>
      {TAB_META.map(({ tab }) => (
        <button
          key={tab}
          type="button"
          data-testid={`tab-${tab}`}
          className={tab === active ? styles.tabButtonActive : styles.tabButton}
          onClick={() => switchHomeTab(appRouter(), tab)}
        >
          <span className={styles.tabLabelBox}>
            {TAB_LABELS[tab]}
            {tab === 'contacts' && applies > 0 && (
              <span className={styles.tabBadge} data-testid="tab-applies">
                {applies > 99 ? '99+' : applies}
              </span>
            )}
          </span>
        </button>
      ))}
    </nav>
  );
}

/**
 * 标题区头像（21am-3，人工给图）。形态与消息气泡头像一致：占位底色按名字 hash
 * 取渐变（avatarColor），有图盖在上面、加载失败自隐藏露出占位字。
 */
function ChatAvatar({ name, avatar }: { name: string; avatar: string }) {
  const cdn = useGlobal((s) => s.config?.cdn ?? '');
  const url = resolveMediaUrl(cdn, avatar);
  return (
    <span className={styles.titleAvatar} style={avatarStyle(name)} data-testid="chat-avatar">
      <span className={styles.titleAvatarChar}>{[...name][0] ?? '?'}</span>
      {url && (
        <img
          className={styles.titleAvatarImg}
          src={url}
          alt=""
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
    </span>
  );
}

/**
 * 私聊标题副标题（14.38b；21am-3 加号码段）：`在线态 · 号码`。
 * 两段都可能缺席，拼接与省略口径单点在 chatHeaderModel.dmSubtitle；全空不渲染
 * （条目缺失≠离线，presenceModel 文件头——不留空行，标题高度不跳）。
 *
 * 零 HTTP：在线态来自 WS presence 推送（map），离线时间与**号码**都从已加载的
 * 通讯录册反查——刻意不为一行副标题去拉 friend/info（旧版进聊天页必拉一次，
 * 新版省掉这笔，requests §3 场景 C 不变）。没进过通讯录页时册为空，号码就不显示；
 * 根治要后端在 content_list 补 UserNo/PremiumNo（backend-suggestions）。
 */
function DmSubtitle({ conversationKey }: { conversationKey: string }) {
  const uid = peerUidOfKey(conversationKey);
  const state = useGlobal((s) => presenceOf(s.presence, uid));
  // 订阅册引用而不是在选择器里反查：选择器每次 store 变更都会跑，册有几百人时
  // 不该每帧线性扫一遍（useMemo 只在册换引用时重算）
  const book = useGlobal((s) => s.contacts.book);
  const offlineTime = useMemo(() => offlineTimeOfFriend(book, uid), [book, uid]);
  const account = useMemo(() => friendAccountOf(book, uid), [book, uid]);
  const now = useNowMinute();
  if (uid <= 0) return null;
  const text = dmSubtitle(presenceStatusText(state, offlineTime, now), account);
  if (text === '') return null;
  return (
    <span className={styles.titleSubtitle} data-testid="chat-subtitle">
      {text}
    </span>
  );
}

/**
 * 群聊标题副标题（21am-3，人工给图「281 位成员 · 557466」）：成员数 + 群号
 * （靓号优先）。快照未到时两段都空 → 不渲染，拿到后自然补上，标题不跳行。
 *
 * **零新增 HTTP**：`chat/groupProfile/load` 与打开群聊必调的 `chat/composer/load`
 * 共用 Worker 会话期缓存，且 `ensureGroupProfile` 有在途合并（session.ts）——
 * 两个 RPC 并发也只落一次 group/profile（requests §3 场景 E 预算不变）。
 */
function GroupSubtitle({ profile }: { profile: GroupProfileSnapshot | null }) {
  if (profile === null) return null;
  const text = groupSubtitle(profile.count, profile.groupNo, profile.premiumNo);
  if (text === '') return null;
  return (
    <span className={styles.titleSubtitle} data-testid="chat-subtitle">
      {text}
    </span>
  );
}

export function ChatPage({ conversationKey }: { conversationKey: string }) {
  // 交互反馈打点（20c）：进会话/切会话的反馈终点——聊天页壳（标题+输入栏）已上屏。
  // 写在渲染路径里而不是 effect 里（晚一帧以上），没在飞的 span 时是 no-op；
  // token=会话 key 保证 A 的 span 不被 B 的渲染结掉。消息行到位是后续里程碑
  // （chat-rows，在 MessageList 结）。
  endInteraction([INTERACTIONS.openChat, INTERACTIONS.switchChat], conversationKey);
  const isMobile = useGlobal((s) => s.layout.isMobile);
  // 资料面板是否开着（21q）：按投影判，不另存一份 UI 状态——面板的开合**就是**导航栈
  const infoOpen = useGlobal((s) => activePanelRoute(s.nav) !== undefined);
  const canGoBack = useGlobal((s) => s.nav.stack.length > 1);
  // 标题口径与会话行一致（conversationRowModel：friendNote 优先 nickname）；
  // 本地无该会话记录（深链到未同步会话）时回退显示 key，对账后自然矫正。
  const rowTitle = useGlobal((s) => {
    const rec = s.conversations.rows?.find((r) => r.key === conversationKey);
    return rec ? rec.remote.friendNote || rec.remote.nickname : '';
  });
  // 会话行没名字（空名占位行：自己他端回显/信令建的行——帧里就没有对方昵称，
  // 且 bumpConversation 之后不回填）时退到通讯录册反查，再不行才是裸 key
  // （chatHeaderModel.friendNameOf；号码段本来就走这条路，零新增请求）
  const bookForTitle = useGlobal((s) => s.contacts.book);
  const title = useMemo(() => {
    if (rowTitle !== '') return rowTitle;
    return friendNameOf(bookForTitle, peerUidOfKey(conversationKey));
  }, [rowTitle, bookForTitle, conversationKey]);
  // 标题区头像与等级徽标（21am-3）：都已在 content_list 行里，零新增请求
  // （ConversationRemote 有 avatar / vipLevel / groupLevel）。
  const headerAvatar = useGlobal((s) => {
    const rec = s.conversations.rows?.find((r) => r.key === conversationKey);
    return rec ? rec.remote.avatar : '';
  });
  const headerVipLevel = useGlobal((s) => {
    const rec = s.conversations.rows?.find((r) => r.key === conversationKey);
    return rec ? rec.remote.vipLevel : 0;
  });
  const headerGroupLevel = useGlobal((s) => {
    const rec = s.conversations.rows?.find((r) => r.key === conversationKey);
    return rec ? rec.remote.groupLevel : 0;
  });
  // 阅后即焚（14.13a）：私聊会话的 afterViewTime → 文本发送 envelope 盖 clock
  //（媒体链在 Worker 侧自读会话记录，同一存储来源不分叉）；33 信令/设置成功
  // 的 upsert 推送会自动刷新此值。徽章/选择器 UI 归 14.13c。
  const afterViewSeconds = useGlobal((s) => {
    if (!conversationKey.startsWith('dm:')) return 0;
    const rec = s.conversations.rows?.find((r) => r.key === conversationKey);
    return rec?.remote.afterViewTime ?? 0;
  });
  // 端到端加密态（14.14b）：timeline open 回包写入，setEncryptionKey 回包更新
  //（密钥纯本地无推送通道，messagesState 口径）
  const encryptionOn = useGlobal(
    (s) => s.messages.timeline?.key === conversationKey && s.messages.timeline.encryptionOn,
  );
  // 群输入栏限制态（14.15b）：loadComposer 回包/`chat/composer` 推送写进
  // timeline.composer；null（私聊/拉取失败/未回包）按不限制渲染（fail-open，
  // 服务端兜底拒发，groupRestrictionModel 文件头）。
  const restriction = useGlobal(
    (s) =>
      (s.messages.timeline?.key === conversationKey ? s.messages.timeline.composer : null) ??
      UNRESTRICTED,
  );
  const timelineReady = useGlobal((s) => s.messages.timeline?.key === conversationKey);
  // 打开群会话读一次限制态（timeline open 落定后；Worker 按 WS 会话期缓存命中
  // 零 HTTP，requests §3 注 12）。失败保持 null 不在本次打开内重试（pins 同口径，
  // 重开重试）；切会话回来 ref 换 key 再读（届时命中缓存）。
  const composerLoadedForRef = useRef('');
  useEffect(() => {
    if (!timelineReady || !conversationKey.startsWith('group:')) return;
    if (composerLoadedForRef.current === conversationKey) return;
    composerLoadedForRef.current = conversationKey;
    void messagesConnection().loadComposer();
  }, [timelineReady, conversationKey]);
  /**
   * 群标题的成员数/群号（21am-3）：与上面的 loadComposer 共用 Worker 会话期缓存，
   * `ensureGroupProfile` 有在途合并 → 两个 RPC 并发仍只落一次 group/profile
   * （场景 E 预算不变）。失败/非群聊保持 null，副标题不渲染，不在本次打开内重试
   * （与 composer、pins 同口径：重开重试）。
   */
  const [groupProfile, setGroupProfile] = useState<GroupProfileSnapshot | null>(null);
  useEffect(() => {
    setGroupProfile(null);
    if (!timelineReady || !conversationKey.startsWith('group:')) return;
    let alive = true;
    void contactsConnection()
      .loadGroupProfile(conversationKey as ConversationKey)
      .then(
        (p) => {
          // 切会话竞态：回包晚于切换时丢弃（epoch 用 alive 闭包表达即可）
          if (alive) setGroupProfile(p);
        },
        () => undefined,
      );
    return () => {
      alive = false;
    };
  }, [timelineReady, conversationKey]);
  // 返回按钮口径（5c 定稿）：移动布局一律显示（普通浏览器与 standalone 同款，
  // 原生 App 观感；standalone 下这是必需品），栈底不显示；桌面双栏导航靠左栏，
  // 不渲染返回按钮（桌面 standalone PWA 同理）。
  // 软键盘（10d）：移动端键盘覆盖 visual viewport 不缩 100dvh，用 inset 顶起
  // 输入栏；列表容器随之变矮，VirtualList 粘底时自动保持底部可见。
  // 页根元素 ref：inset 要量它的 rect 底边（21ah-3，见 useKeyboardInset）
  const pageRef = useRef<HTMLDivElement>(null);
  const kbInset = useKeyboardInset(isMobile, pageRef);
  // 媒体发送（12b/14.3b）：按钮/粘贴（Composer）与 PC 拖拽共用一个发送器与提示条；
  // 拖拽收任意文件（图片/视频/文件自动分流，旧版拖入非图片同样成文件消息）
  const imageSender = useImageSender();
  // 群媒体限制拦截提示（14.15b）：Composer 入口拦截回调 + 拖拽/选择器混投过滤
  // 共用一条 3s 提示（editTip 同款）；禁言期一切媒体路径折「禁言中」（旧版 toast
  // 同文案）。入口级拦截在 Composer，这里是粘贴/拖拽等旁路的最后门禁。
  const [mediaTip, setMediaTip] = useState('');
  const mediaTipTimerRef = useRef(0);
  const showMediaTip = useCallback((text: string) => {
    clearTimeout(mediaTipTimerRef.current);
    setMediaTip(text);
    mediaTipTimerRef.current = window.setTimeout(() => setMediaTip(''), 3000);
  }, []);
  useEffect(() => () => clearTimeout(mediaTipTimerRef.current), []);
  const onMediaBlocked = useCallback(
    (msgType: number) => showMediaTip(blockedMediaText(msgType)),
    [showMediaTip],
  );
  const sendFiles = imageSender.sendFiles;
  const sendAttachmentsRaw = imageSender.sendAttachments;
  // 发图确认页（14.33a）：选择器/粘贴先进确认浮层（可逐张移除，编辑器归 14.33b），
  // 确认后原路交回 sendFiles——拦截点在「拦截检查之后」，禁言/禁图仍是当场提示
  // 不开浮层。PC 拖拽刻意不走确认页（imageConfirmModel 文件头有记）。
  const [pendingImages, setPendingImages] = useState<readonly File[] | null>(null);
  const onSendImages = useCallback(
    (files: File[]) => {
      if (restriction.muted) return showMediaTip('禁言中');
      if (isMediaBlocked(restriction, MsgType.Image)) return onMediaBlocked(MsgType.Image);
      setPendingImages(files);
    },
    [restriction, showMediaTip, onMediaBlocked],
  );
  // 切会话清待确认（桌面双栏 ChatPage 不随会话 remount；浮层本身是模态，
  // 正常走不到这里，纯兜底防把 A 会话的选图发进 B）
  useEffect(() => setPendingImages(null), [conversationKey]);
  const onConfirmImages = useCallback(
    (files: File[]) => {
      setPendingImages(null);
      sendFiles(files);
    },
    [sendFiles],
  );
  const onCancelImages = useCallback(() => setPendingImages(null), []);
  // 名片发送（21d-2）：⊕ 托盘「名片」→ 选好友 → msgType 3 发进当前会话。
  // envelope 与好友详情页的「分享名片」同一个构造（buildContactCardEnvelope），
  // clock 取**当前会话**的阅后即焚时长（私聊才有；群聊恒 0，与 14.13a 同口径）。
  const [cardPicking, setCardPicking] = useState(false);
  const [cardSending, setCardSending] = useState(false);
  const sendContactCard = useCallback(
    (friend: ContactFriend) => {
      setCardSending(true);
      const name = friend.friendNote || friend.nickname || friend.username;
      const clock = conversationKey.startsWith('dm:') ? afterViewSeconds : 0;
      const content = buildContactCardEnvelope(
        friend.friendId,
        name,
        stripMediaDomain(friend.avatar),
        friend.username,
        clock,
      );
      void messagesConnection()
        .sendTo(conversationKey as ConversationKey, { msgType: MsgType.Card, content })
        .then(() => {
          setCardSending(false);
          setCardPicking(false);
        });
    },
    [conversationKey, afterViewSeconds],
  );
  const onSendAttachments = useCallback(
    (files: File[]) => {
      if (restriction.muted) return showMediaTip('禁言中');
      const { allowed, blockedTypes } = filterBlockedAttachments(files, restriction);
      const firstBlocked = blockedTypes[0];
      if (firstBlocked !== undefined) onMediaBlocked(firstBlocked);
      if (allowed.length > 0) sendAttachmentsRaw(allowed);
    },
    [restriction, sendAttachmentsRaw, showMediaTip, onMediaBlocked],
  );
  const { dragging, bind } = useImageDrop(!isMobile, onSendAttachments);
  // 引用回复（14.6）：菜单「回复」→ Composer 预览条 → 发送帧带 reply ext。
  // 状态记会话 key：切会话自动失效（ChatPage 桌面布局下不随会话 remount）；
  // 刷新即逝与旧版同级（不进草稿，14.12 接入后再议）。
  const [reply, setReply] = useState<{ convKey: string; ref: ReplyRef } | null>(null);
  const [editing, setEditing] = useState<{ convKey: string; draft: ComposerEditDraft } | null>(
    null,
  );
  const replyDraft = reply !== null && reply.convKey === conversationKey ? reply.ref : null;
  const onReply = useCallback(
    (item: MessageItemVM) => {
      setEditing(null); // 回复与编辑互斥（旧版同口径：进另一态即退出当前态）
      setReply({
        convKey: conversationKey,
        // 快照口径＝旧版：name 取发送者昵称（自己发的行昵称可空，渲染兜底）。
        // 21ah-2 起还带原消息 msgType+正文 envelope（阅后即焚/加密除外，口径在
        // replySnapshotRef），接收端跳不过去时能原样渲染而不是只剩「[图片]」。
        ref: replySnapshotRef({
          id: item.id,
          name: item.nickname,
          content: item.content,
          secret: item.secret,
          encrypted: item.encrypted,
          decryptFailed: item.decryptFailed,
        }),
      });
    },
    [conversationKey],
  );
  const onCancelReply = useCallback(() => setReply(null), []);
  // 消息编辑（14.7b）：菜单「编辑」→ Composer 编辑条（暂存草稿在 Composer 内）
  // → 提交走 chat/edit（乐观替换由 Worker 推送，14.7a）。状态记会话 key 与引用
  // 态同口径：切会话自动失效。失败两路同一 toast：受理 false（WS 断/校验不过）
  // 当场提示；异步失败（error_msg/超时）行已回滚，editFailedSeq 变化提示。
  const editDraft = editing !== null && editing.convKey === conversationKey ? editing.draft : null;
  const [editTip, setEditTip] = useState('');
  const editTipTimerRef = useRef(0);
  const showEditTip = useCallback((text: string) => {
    clearTimeout(editTipTimerRef.current);
    setEditTip(text);
    editTipTimerRef.current = window.setTimeout(() => setEditTip(''), 3000);
  }, []);
  useEffect(() => () => clearTimeout(editTipTimerRef.current), []);
  const onEdit = useCallback(
    (item: MessageItemVM) => {
      if (item.content.kind !== 'text') return;
      setReply(null);
      setEditing({
        convKey: conversationKey,
        draft: { msgId: item.id, text: item.content.text },
      });
    },
    [conversationKey],
  );
  const onCancelEdit = useCallback(() => setEditing(null), []);
  // 阅后即焚设置失败提示（14.13c）：editTip 同款 3s 提示条
  const [burnTip, setBurnTip] = useState(false);
  const burnTipTimerRef = useRef(0);
  const onBurnError = useCallback(() => {
    clearTimeout(burnTipTimerRef.current);
    setBurnTip(true);
    burnTipTimerRef.current = window.setTimeout(() => setBurnTip(false), 3000);
  }, []);
  useEffect(() => () => clearTimeout(burnTipTimerRef.current), []);
  // 多选态（14.9，状态在 MessageList——按会话 remount 切会话自动退出，这里
  // mount 时回调 false 复位）：隐藏输入栏（底部批量工具栏顶位）、清引用/编辑态
  // （旧版 _enterMessageSelectionMode 同口径）
  const [selecting, setSelecting] = useState(false);
  /** Composer 暴露的聚焦入口（21x） */
  const composerFocusRef = useRef<(() => void) | null>(null);
  /**
   * 打开/切换会话即聚焦输入框（21z，人工：「消息列表这里点开输入框不聚焦啊」）。
   * **仅桌面**：移动端进会话就弹软键盘会把半屏消息顶走，且返回时还要收回去。
   * 多选态不抢（输入栏那会儿是藏着的）；被禁言时 textarea 是 disabled，
   * `focus()` 本就是 no-op，不额外判。
   * Composer 带 `key={会话}` 会随会话重挂，子组件 effect 先于父组件跑，
   * 所以这里读到的 focusRef 已经是**新那一个** Composer 的。
   */
  useEffect(() => {
    if (isMobile || selecting) return;
    composerFocusRef.current?.();
  }, [conversationKey, isMobile, selecting]);
  const onBodyClick = useCallback(
    (e: { target: EventTarget | null }) => {
      if (!focusComposerOnBodyClick(e, { isMobile, selecting })) return;
      composerFocusRef.current?.();
    },
    [isMobile, selecting],
  );
  const onSelectingChange = useCallback((on: boolean) => {
    setSelecting(on);
    if (on) {
      setReply(null);
      setEditing(null);
    }
  }, []);
  const onSubmitEdit = useCallback(
    (text: string) => {
      const target = editDraft;
      if (target === null) return;
      void messagesConnection()
        .editMessage(target.msgId, text)
        .then((ok) => {
          if (ok) setEditing(null);
          else showEditTip('编辑未发送，请检查网络后重试');
        });
    },
    [editDraft, showEditTip],
  );
  // 异步编辑失败 toast：首次观测/切会话只记基线不提示（timeline 重开 seq 归零，
  // 小于基线同样只重置）
  const editFailedSeq = useGlobal((s) =>
    s.messages.timeline?.key === conversationKey ? s.messages.timeline.editFailedSeq : 0,
  );
  // 服务端原文（21f）；空=回落通用文案，旧版 `error.message.isEmpty ? '编辑失败' : ...`
  const editFailedText = useGlobal((s) =>
    s.messages.timeline?.key === conversationKey ? s.messages.timeline.editFailedText : '',
  );
  const editFailBaseRef = useRef<{ key: string; seq: number } | null>(null);
  useEffect(() => {
    const base = editFailBaseRef.current;
    editFailBaseRef.current = { key: conversationKey, seq: editFailedSeq };
    if (base === null || base.key !== conversationKey) return;
    if (editFailedSeq > base.seq) {
      showEditTip(editFailedText !== '' ? editFailedText : '编辑失败，内容已还原');
    }
  }, [editFailedSeq, editFailedText, conversationKey, showEditTip]);
  return (
    <div
      ref={pageRef}
      className={styles.page}
      data-testid="chat-page"
      data-conversation={conversationKey}
      style={kbInset > 0 ? { paddingBottom: `${kbInset}px` } : undefined}
      {...bind}
    >
      <header className={styles.pageHeader}>
        {isMobile && canGoBack && (
          <BackButton testid="chat-back" onClick={() => appRouter().back()} />
        )}
        {/* 标题即入口：私聊进好友详情（14.21b）、群聊进群设置主页（14.25b） */}
        <button
          type="button"
          className={styles.titleButton}
          data-testid="chat-title"
          onClick={() => {
            if (conversationKey.startsWith('dm:')) {
              openFriendDetail(appRouter(), Number(conversationKey.slice('dm:'.length)));
            } else {
              openGroupSettings(appRouter(), Number(conversationKey.slice('group:'.length)));
            }
          }}
        >
          <ChatAvatar name={title || conversationKey} avatar={headerAvatar} />
          <span className={styles.titleMain}>
            <span className={styles.titleLine}>
              <span className={styles.titleText}>{title || conversationKey}</span>
              {/* 等级徽标（21am-3）：私聊看 vipLevel、群聊看 groupLevel，两者互斥
                  （vipBadge.conversationVipBadge 同口径）。level<=0 时返回 null 不渲染 */}
              <VipBadge
                view={
                  conversationKey.startsWith('group:')
                    ? groupVipBadge(headerGroupLevel)
                    : userVipBadge(headerVipLevel)
                }
              />
              {/* 靓号徽标：群聊取快照的 premiumNo；私聊 content_list 不下发靓号，
                  只能等后端补字段，这里恒 null 不渲染（见 chatHeaderModel 文件头） */}
              <PremiumBadge
                view={premiumNoBadge(groupProfile === null ? '' : groupProfile.premiumNo)}
              />
            </span>
            {conversationKey.startsWith('group:') ? (
              <GroupSubtitle profile={groupProfile} />
            ) : (
              <DmSubtitle conversationKey={conversationKey} />
            )}
          </span>
        </button>
        {/* 资料面板开关（21q，仅桌面）：ⓘ 开、× 收，与人工给的图一致。
            移动端没有并存形态，这个键不渲染（标题点击照旧进详情页） */}
        {!isMobile && (
          <button
            type="button"
            className={styles.infoToggle}
            data-testid="chat-info-toggle"
            data-on={infoOpen ? 'true' : undefined}
            aria-label={infoOpen ? '收起资料' : '查看资料'}
            title={infoOpen ? '收起资料' : '查看资料'}
            onClick={() => toggleConversationInfo(appRouter(), conversationKey)}
          >
            {infoOpen ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="8.4" />
                <path d="M12 11v5.2" />
                <path d="M12 7.9v.1" />
              </svg>
            )}
          </button>
        )}
        {/* 阅后即焚徽章（14.13c，仅私聊）：旧版关闭时不渲染（入口在好友详情，
            14.21），新版恒显作为唯一入口（AfterViewControl 文件头有记） */}
        {conversationKey.startsWith('dm:') && (
          <>
            <AfterViewControl
              seconds={afterViewSeconds}
              mobile={isMobile}
              onError={onBurnError}
            />
            {/* 端到端加密锁徽章（14.14b）：火焰徽章同款恒显入口；失败提示复用
                burnTip（同是「设置失败」口径） */}
            <EncryptionControl on={encryptionOn} mobile={isMobile} onError={onBurnError} />
          </>
        )}
      </header>
      {/* 点消息区把焦点送回输入框（21x，人工要求「点击消息默认聚焦输入框」）。
          判据都写在 focusComposerOnBodyClick，别在这里再加分支 */}
      <div
        className={`${styles.pageBodyFill} ${styles.chatBody}`}
        data-testid="chat-body"
        onClick={onBodyClick}
      >
        {/* key=会话：MessageList/VirtualList 整体 remount，anchor/测高缓存按会话隔离。
            ⚠️ 背景渐变在**外层**这个不 remount 的容器上（21m），别挪回组件内部 */}
        <MessageList
          key={conversationKey}
          conversationKey={conversationKey}
          onReply={onReply}
          onEdit={onEdit}
          onSelectingChange={onSelectingChange}
        />
      </div>
      {imageSender.tip !== '' && (
        <div className={styles.imageSendTip} data-testid="image-send-tip">
          {imageSender.tip}
        </div>
      )}
      {editTip !== '' && (
        <div className={styles.imageSendTip} data-testid="edit-failed-tip">
          {editTip}
        </div>
      )}
      {burnTip && (
        <div className={styles.imageSendTip} data-testid="burn-failed-tip">
          设置失败，请检查网络后重试
        </div>
      )}
      {mediaTip !== '' && (
        <div className={styles.imageSendTip} data-testid="media-blocked-tip">
          {mediaTip}
        </div>
      )}
      {/* key=会话：切会话不串输入内容（草稿按会话存取，14.12）。多选期隐藏但保持
          挂载（display:contents 不参与布局）——退出多选输入内容原样回来 */}
      <div style={{ display: selecting ? 'none' : 'contents' }}>
        <Composer
          key={`composer:${conversationKey}`}
          conversationKey={conversationKey}
          onSendImages={onSendImages}
          onSendAttachments={onSendAttachments}
          replyTo={replyDraft}
          onCancelReply={onCancelReply}
          editing={editDraft}
          onCancelEdit={onCancelEdit}
          onSubmitEdit={onSubmitEdit}
          isGroup={conversationKey.startsWith('group:')}
          afterViewSeconds={afterViewSeconds}
          restriction={restriction}
          onMediaBlocked={onMediaBlocked}
          onPickContactCard={() => setCardPicking(true)}
          focusRef={composerFocusRef}
        />
      </div>
      {dragging && (
        <div className={styles.dropOverlay} data-testid="drop-overlay">
          松开发送
        </div>
      )}
      {cardPicking && (
        <ContactCardPickerLazy
          mobile={isMobile}
          busy={cardSending}
          onPick={sendContactCard}
          onClose={() => setCardPicking(false)}
        />
      )}
      {pendingImages !== null && (
        <ImageConfirmSheetLazy
          files={pendingImages}
          mobile={isMobile}
          onCancel={onCancelImages}
          onSend={onConfirmImages}
        />
      )}
    </div>
  );
}

export function EmptyChatPane() {
  return (
    <div className={styles.emptyPane} data-testid="empty-chat-pane">
      <p className={styles.placeholder}>选择一个会话开始聊天</p>
    </div>
  );
}
